import { liveReload } from '@shared/services/realtime.service';
import { KpiStripComponent, ListKpi } from '@app/shared/components/kpi-strip/kpi-strip.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SimpleExcelService } from '@app/shared/services/simple-excel.service';
import { ListSelection, ListView } from '@app/shared/utils/list-view';
import { SelectionBarComponent } from '@app/shared/components/selection-bar/selection-bar.component';
import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormsModule } from '@angular/forms';
import { currentMonthStart, currentMonthEnd, inDateRange } from '@app/shared/utils/date-range';
import { FilterChipsComponent, FilterChip } from '@app/shared/components/filter-bar/filter-chips.component';
import { SearchPillComponent } from '@app/shared/components/filter-bar/search-pill.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { Component, OnInit, inject, signal } from '@angular/core';

import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { ModulesService } from '@shared/services/modules.service';
import { ShippingsService } from '@shared/services/entities/shippings.service';
import { WarehousesService } from '@shared/services/entities/warehouse.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { UserScopeService, UserScope, inScope } from '@app/shared/services/user-scope.service';
import { ProductsService } from '@shared/services/entities/products.service';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { ReceptionDialogComponent } from '@app/inventory/reception-dialog/reception-dialog.component';
import { ReceptionRfidDialogComponent } from './reception-rfid-dialog.component';
import { FileManagerDialogComponent } from '@shared/components/file-manager-dialog/file-manager-dialog.component';
import { sortAlphabetically } from '@shared/utils/sort.utils';

/**
 * Módulo de Recepción de material — los procesos "Entrada de material – DLV"
 * y "Entrega de material – otro" del forms de campo, fuera de Inventario.
 *
 * La captura reutiliza el MISMO diálogo y endpoint que ya existían
 * (`ReceptionDialogComponent` → POST /shippings/reception): cada entrada crea
 * el documento en la tabla `shippings` (folio REC-n) + piezas sin etiqueta +
 * bitácora, así que el inventario cuadra igual que antes. Lo que agrega este
 * módulo es el lugar propio para operarlo y la bitácora de entradas; la
 * consulta histórica también vive en Reportes → Entradas de material.
 */
@Component({
  selector: 'app-material-reception',
  standalone: true,
  imports: [KpiStripComponent, MatCheckboxModule, MatButtonToggleModule, SelectionBarComponent, PageLoaderComponent, MatDatepickerModule, FormsModule, SearchPillComponent, FilterChipsComponent, PageTitleComponent, 
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDialogModule,
  ],
  templateUrl: './material-reception.component.html',
  styleUrl: './material-reception.component.scss',
})
export class MaterialReceptionComponent implements OnInit {
  private modulesSvcTitle = inject(ModulesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/materialReception', 'Recepción de material');

  private readonly shippingsService     = inject(ShippingsService);
  private readonly warehousesService    = inject(WarehousesService);
  private readonly placesService        = inject(PlacesService);
  private readonly userScopeService     = inject(UserScopeService);
  private readonly productsService      = inject(ProductsService);
  private readonly customMessageService = inject(CustomMessageService);
  private readonly dialog               = inject(MatDialog);

  loading = signal(false);

  // ─── Catálogos ─────────────────────────────────────────────────────────────
  warehouses: any[] = [];
  private warehouseNames: Record<string, string> = {};
  private warehousePlace: Record<string, string> = {};
  private placeAlias: Record<string, string> = {};
  private productsList: any[] = [];

  /** Almacén destino de la entrada (requerido para capturar). */
  selectedWarehouse: string | null = null;

  /** Ubicación: solo sitios tipo Hospital, activos y del alcance del usuario. */
  hospitals: any[] = [];
  selectedPlace: string | null = null;
  private scope: UserScope = { places: null, specialties: null };

  /** Lockers (almacenes) del hospital elegido. */
  get warehousesOfPlace(): any[] {
    return this.selectedPlace ? this.warehouses.filter((w: any) => w.place === this.selectedPlace) : [];
  }

  onPlaceChange(): void {
    const options = this.warehousesOfPlace;
    if (!options.some((w: any) => w.id === this.selectedWarehouse)) {
      this.selectedWarehouse = options.length === 1 ? options[0].id : null;
    }
  }

  // ─── Bitácora de entradas ──────────────────────────────────────────────────
  // ─── Indicadores (como en el inventario) ───
  private allRows: any[] = [];
  kpis: ListKpi[] = [];
  activeKpi: string | null = null;
  private static readonly KPI_FILTERS: Record<string, (r: any) => boolean> = {
    complete: r => Number(r?._pieces) > 0 && Number(r?._tagged) >= Number(r?._pieces),
    pending:  r => !(Number(r?._pieces) > 0 && Number(r?._tagged) >= Number(r?._pieces)),
    unsigned: r => !r?._signed,
  };
  private refreshKpis(): void {
    const all = this.allRows;
    const count = (k: string) => all.filter(MaterialReceptionComponent.KPI_FILTERS[k]).length;
    this.kpis = [
      { key: 'total', label: 'Entradas', icon: 'local_shipping', value: all.length, isTotal: true },
      { key: 'complete', label: 'RFID completo', icon: 'contactless', value: count('complete'), tone: 'ok' },
      { key: 'pending', label: 'Pendientes de RFID', icon: 'pending_actions', value: count('pending'), tone: 'warn' },
      { key: 'unsigned', label: 'Sin documento firmado', icon: 'upload_file', value: count('unsigned'), tone: 'danger' },
    ];
    this.applyKpi();
  }
  setKpi(key: string | null): void { this.activeKpi = key; this.applyKpi(); }
  private applyKpi(): void {
    const f = this.activeKpi ? MaterialReceptionComponent.KPI_FILTERS[this.activeKpi] : null;
    this.dataSource.data = f ? this.allRows.filter(f) : this.allRows;
  }

  /** Vista tabla / tarjetas y selección con checkbox. */
  readonly listView = new ListView('materialReception', 'table');
  readonly sel = new ListSelection<any>(() => (this.dataSource?.filteredData ?? []) as any[]);
  private readonly simpleExcelSel = inject(SimpleExcelService);

  /** Exporta a Excel las entradas seleccionadas. */
  exportSelection(): void {
    const rows = this.sel.selected.map((s: any) => ({
      folio: s.fileNumber ?? '', date: this.formatDate(s.createdOn), place: s._place ?? '', locker: s._warehouse ?? '',
      delivery: s._source ?? '', tagged: s._tagged ?? 0, pieces: s._pieces ?? '',
      signed: s._signed ? 'Sí' : 'No', comments: s.comments ?? '',
    }));
    if (!rows.length) return;
    this.simpleExcelSel.simpleXLSX({
      fileName: 'Entradas de material (selección)',
      sheets: [{ sheetName: 'Selección', header: [], lineBreak: 0, data: rows, columns: [
        { columnKey: 'folio', columnName: 'Folio' }, { columnKey: 'date', columnName: 'Fecha' },
        { columnKey: 'place', columnName: 'Hospital' }, { columnKey: 'locker', columnName: 'Destino' },
        { columnKey: 'delivery', columnName: 'No. de delivery' }, { columnKey: 'tagged', columnName: 'Piezas con RFID' },
        { columnKey: 'pieces', columnName: 'Piezas' }, { columnKey: 'signed', columnName: 'Documento firmado' },
        { columnKey: 'comments', columnName: 'Comentarios' },
      ] }],
    } as any);
  }

  columns = ['select', 'fileNumber', 'createdOn', 'place', 'warehouse', 'source', 'pieces', 'backorder', 'comments', 'actions'];
  dataSource = new MatTableDataSource<any>([]);

  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();
  /** Tiempo real: recarga cuando otro usuario cambia estos módulos (WebSocket). */
  private readonly live = liveReload(['shippings'], () => this.pageLoad.ready && this.pageLoad.refresh(this.loadEntries()));

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    await Promise.all([this.loadWarehouses(), this.loadProducts(), this.loadHospitals()]);
    await this.loadEntries();
  }

  private async loadWarehouses(): Promise<void> {
    try {
      await this.warehousesService.search({ searchType: 'default', condition: { deleted: false, active: true } });
      this.warehouses = sortAlphabetically(this.warehousesService.listData(), 'name');
      this.warehouseNames = Object.fromEntries(this.warehouses.map((w: any) => [w.id, w.name]));
      this.warehousePlace = Object.fromEntries(this.warehouses.map((w: any) => [w.id, w.place]));
    } catch {
      console.error('[Recepción] No se pudieron cargar los almacenes');
    }
  }

  private async loadHospitals(): Promise<void> {
    try {
      try { this.scope = await this.userScopeService.load(); } catch { /* sin alcance: ve todo */ }
      const resp: any = await this.placesService.search({ searchType: 'default', condition: { deleted: false } });
      this.placeAlias = Object.fromEntries(((resp?.data ?? []) as any[]).map(p => [p.id, p.alias || p.name || '']));
      this.hospitals = ((resp?.data ?? []) as any[])
        .filter(p => String(p?.type ?? '').toUpperCase() === 'HOSPITAL'
          && (p?.active === true || String(p?.active).toLowerCase() === 'true')
          && inScope(this.scope.places, p?.id, false))
        .sort((a, b) => String(a.alias || a.name || '').localeCompare(String(b.alias || b.name || ''), 'es', { sensitivity: 'base', numeric: true }));
      if (this.hospitals.length === 1) { this.selectedPlace = this.hospitals[0].id; this.onPlaceChange(); }
    } catch {
      console.error('[Recepción] No se pudieron cargar las ubicaciones');
    }
  }

  private async loadProducts(): Promise<void> {
    try {
      await this.productsService.search({ searchType: 'default', condition: { deleted: false, active: true } });
      this.productsList = this.productsService.listData();
    } catch {
      console.error('[Recepción] No se pudo cargar el catálogo de productos');
    }
  }

  /** Periodo de la lista: por omisión el mes actual; se amplía con el filtro. */
  periodStart: Date | null = currentMonthStart();
  periodEnd: Date | null = currentMonthEnd();
  get periodChips(): FilterChip[] {
    const c: FilterChip[] = [];
    if (this.periodStart || this.periodEnd) {
      const f = (d: Date | null) => d ? new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '…';
      c.push({ key: 'period', label: 'Periodo', value: f(this.periodStart) + ' – ' + f(this.periodEnd), removable: false });
    }
    return c;
  }
  async resetPeriod(): Promise<void> {
    this.periodStart = currentMonthStart(); this.periodEnd = currentMonthEnd();
    await this.loadEntries();
  }

  /** Búsqueda de texto sobre la bitácora de entradas. */
  applySearch(value: string): void {
    this.dataSource.filter = (value ?? '').trim().toLowerCase();
  }

  async loadEntries(): Promise<void> {
    this.loading.set(true);
    try {
      const response: any = await this.shippingsService.search({
        searchType: 'default',
        condition: { deleted: false, type: 'ENTRADA' },
      });
      const rows = (response?.data ?? []).map((s: any) => ({
        ...s,
        _warehouse: this.warehouseNames[s.toWarehouse] ?? this.warehouseNames[s.warehouse] ?? '',
        _place: this.placeAlias[s.toPlace ?? s.place ?? this.warehousePlace[s.toWarehouse ?? s.warehouse]] ?? '',
        _source: s.deliveryFolio || s.origin || '',
        _tagged: Array.isArray(s.rfid) ? s.rfid.length : 0,
        _backorder: (Array.isArray(s.backorder) ? s.backorder : []).reduce((a: number, b: any) => a + (Number(b?.quantity) || 0), 0),
        _backorderText: (Array.isArray(s.backorder) ? s.backorder : []).map((b: any) => `${b.code} ×${b.quantity}`).join(', '),
        _extra: (Array.isArray(s.extra) ? s.extra : []).reduce((a: number, b: any) => a + (Number(b?.quantity) || 0), 0),
        _extraText: (Array.isArray(s.extra) ? s.extra : []).map((b: any) => `${b.code} ×${b.quantity}`).join(', '),
        _signed: (Array.isArray(s.attachments) ? s.attachments : []).filter((a: any) => a?.type === 'FIRMADO').slice(-1)[0] ?? null,
        _pieces: Array.isArray(s.items)
          ? s.items.reduce((sum: number, it: any) => sum + (Number(it?.quantity) || 0), 0)
          : '',
      }));
      // Más recientes primero.
      rows.sort((a: any, b: any) => String(b.createdOn ?? '').localeCompare(String(a.createdOn ?? '')));
      this.allRows = rows.filter((r: any) => inDateRange(r.createdOn, this.periodStart, this.periodEnd));
      this.refreshKpis();
    } catch {
      await this.customMessageService.open({
        title: 'Error', type: 'error',
        message: 'No se pudo cargar la bitácora de entradas.', dismissBtn: true,
      });
    } finally {
      this.loading.set(false);
    }
  }

  /** Entrada de material – DLV (isOther=false) o Entrega – otro (isOther=true). */
  openReception(isOther: boolean): void {
    if (!this.selectedPlace || !this.selectedWarehouse) {
      this.customMessageService.open({
        title: 'Advertencia', type: 'warning',
        message: 'Selecciona la ubicación (hospital) y el locker que recibe el material.', dismissBtn: true,
      });
      return;
    }
    const ref = this.dialog.open(ReceptionDialogComponent, {
      disableClose: true,
      width: '860px',
      maxWidth: '95vw',
      data: {
        warehouse: this.selectedWarehouse,
        place: this.selectedPlace,
        warehouseName: this.warehouseNames[this.selectedWarehouse] ?? '',
        productsList: this.productsList,
        isOther,
      },
    });
    ref.afterClosed().subscribe(async (ok: any) => {
      if (ok === true) await this.loadEntries();
    });
  }

  formatDate(value: string): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? String(value) : d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  }

  // ─── Acciones de la bitácora ─────────────────────────────────────────────

  /**
   * Documento firmado (foto o PDF) con el gestor de archivos compartido:
   * cargar, ver, descargar, reemplazar o quitar. La key S3 se guarda en
   * `attachments` de la entrada como tipo FIRMADO.
   */
  openSigned(entry: any): void {
    const ref = this.dialog.open(FileManagerDialogComponent, {
      data: {
        value:       entry?._signed?.key ?? '',
        label:       `Documento firmado · ${entry.fileNumber ?? ''}`,
        accept:      'image/*,.pdf,application/pdf',
        uploadPath:  'receptions',
        uploadFolio: entry.id,
      },
      width: '680px', maxHeight: '90vh',
    });
    ref.afterClosed().subscribe(async (key: string | null) => {
      if (key === null || key === undefined) return;   // cancelado
      if (key === (entry?._signed?.key ?? '')) return; // sin cambios
      const others = (Array.isArray(entry.attachments) ? entry.attachments : []).filter((a: any) => a?.type !== 'FIRMADO');
      const attachments = key
        ? [...others, { type: 'FIRMADO', key, fileName: String(key).split('/').pop(), uploadedOn: new Date().toISOString() }]
        : others;
      try {
        await this.shippingsService.update({ id: entry.id, attachments, operationType: 'single' } as any);
        await this.customMessageService.open({
          title: '¡Hecho!', type: 'success', duration: 2000,
          message: key ? `Documento firmado de <b>${entry.fileNumber}</b> guardado.` : `Se quitó el documento firmado de <b>${entry.fileNumber}</b>.`,
        });
        await this.loadEntries();
      } catch (err) {
        await this.customMessageService.open({ title: 'Error', type: 'error', message: 'No se pudo guardar el documento: ' + String(err), dismissBtn: true });
      }
    });
  }

  /** Líneas por unidad → etiqueta RFID → piezas en el locker de la entrada. */
  openRfid(entry: any): void {
    const productsById = Object.fromEntries(this.productsList.map((p: any) => [p.id, p]));
    const ref = this.dialog.open(ReceptionRfidDialogComponent, {
      width: '980px', maxWidth: '96vw', maxHeight: '90vh', disableClose: true,
      data: { shipping: { ...entry }, productsById, warehouseName: entry._warehouse || '' },
    });
    ref.afterClosed().subscribe(async changed => { if (changed) await this.loadEntries(); });
  }
}
